/**
 * BrandLogo — a deployment's logo, rendered as an image that cannot execute.
 *
 * `BrandTokens.logo` is a string of markup and it is DATA: it arrives from the
 * deployment's `platform|brand` setting (BrandSync), from a portal's `branding`
 * patch (`portal.set_branding`), or from a file a user drops into the branding
 * panel. Whoever edits branding is not necessarily whoever is signed in — and
 * the logo renders in the topbar, on EVERY page.
 *
 * It used to render through `dangerouslySetInnerHTML`. SVG is a script-carrying
 * format (`<foreignObject>`, `<animate onbegin>`, `<use>`), the field never
 * required SVG at all — a bare `<img src=x onerror=…>` was accepted just as
 * happily — and the markup landed in the console document itself, with no
 * iframe boundary of any kind. That is a persistent XSS on every page for
 * anyone with branding authority (SEC-17 / #374).
 *
 * The fix is not a sanitiser. An SVG allowlist is a thing we would have to keep
 * correct forever against a parser we do not own, and it is unnecessary here:
 * branding IS just an image. An SVG loaded through `<img src>` is rendered in
 * the browser's restricted mode — no scripting, no external fetches, no access
 * to the embedding document — so the logo looks identical and the markup stops
 * being code.
 *
 * The one visible consequence: a logo relying on `currentColor` can no longer
 * inherit the topbar's ink, because an `<img>` has no CSS relationship with the
 * page. Every shipped logo is self-coloured already (the Moonbase monogram
 * carries its own gradient), which is what the token's own doc-comment asks
 * for — "self-coloured so it reads on light AND dark".
 *
 * ── why it lives in @solum/ui and no longer in apps/console ─────────────────
 *
 * The sign-in screen (`@solum/auth`) renders a brand's logo too, and it is the
 * one surface where the logo is shown to a person who is NOT signed in — i.e.
 * to a customer's own consumers on a white-labelled portal (#125). An app
 * cannot import from another app, so leaving this in the console would have
 * meant a second `brandLogoSrc` in the auth package: two implementations of the
 * decision that stops a brand token being executable, and only one of them
 * covered by the mutation-tested suite next door. Moved rather than copied.
 */

import { brandImageSrc } from './BrandAsset'

/**
 * The `src` for a brand logo, or null when there is nothing safe to render.
 *
 * Markup is wrapped as an `image/svg+xml` data URI — the media type is FIXED
 * here, so a payload cannot talk the browser into parsing it as HTML no matter
 * what it contains. A value that is already a reference is passed through only
 * when it is an inert image URI or an http(s) URL; anything else (`javascript:`,
 * `data:text/html`, a bare word) returns null rather than being guessed at.
 */
export function brandLogoSrc(logo: string | null | undefined): string | null {
  return brandImageSrc(logo)
}

export function BrandLogo({ logo, className, testId }: {
  logo: string | null | undefined
  className?: string
  testId?: string
}) {
  const src = brandLogoSrc(logo)
  if (src === null) return null
  // aria-hidden + empty alt: the brand NAME sits next to it in the lockup, so
  // the logo is decoration to a screen reader — announcing it twice is noise.
  return <img className={className} src={src} alt="" aria-hidden="true" data-testid={testId} />
}
